<template>
   <div>
        <app-field :error="error" :classes="classes" :append="append">
            <app-label v-if="label != ''" :label="label" :required="required" :description="description"></app-label>

            <app-button element="button" class="btn  btn--primary  btn--primary--sml" @click="modalOpen = true">Edit</app-button>
        </app-field>

        <app-modal v-if="modalOpen" tabindex="-1" role="dialog" @modal-close="closeModal">
            <div
                class="modal__box  modal__box--xlarge text-left"
            >
                <div class="p-6 bg-grey-lightest border-b border-grey-lighter">
                    <h2 v-html="name ? name : 'Code'"></h2>
                </div>
                <div>
                    <div class="p-12">
                        <app-codemirror
                            class="w-full"
                            v-model="content"
                            v-bind="$attrs"
                            :options="cmOptions"
                            @input="handleChange"
                        />
                    </div>
                </div>
                <div class="modal__footer">
                    <div class="flex items-center ml-auto">
                        <app-button element="button" class="btn  btn--secondary  btn--secondary--sml" @click="modalOpen = false">Save</app-button>
                    </div>
                </div>
            </div>
        </app-modal>
    </div>
</template>

<script>
    import { codemirror } from 'vue-codemirror';
    import Button from '@/components/Button';
    import Modal from '@/components/Modal';
    import TrixBase from '@/components/TrixBase';
    import Field from '@/components/fields/Field';
    import Label from '@/components/fields/Label';
    import 'codemirror/lib/codemirror.css';

    // language
    import 'codemirror/mode/xml/xml.js'
    import 'codemirror/mode/javascript/javascript.js'
    import 'codemirror/mode/css/css.js'
    import 'codemirror/mode/sass/sass.js'
    import 'codemirror/mode/php/php.js'
    import 'codemirror/mode/markdown/markdown.js'
    import 'codemirror/mode/nginx/nginx.js'
    import 'codemirror/mode/shell/shell.js'
    import 'codemirror/mode/vue/vue.js'
    import 'codemirror/mode/yaml/yaml.js'

    // require active-line.js
    import'codemirror/addon/selection/active-line.js'
    // styleSelectedText
    import'codemirror/addon/selection/mark-selection.js'
    import'codemirror/addon/search/searchcursor.js'
    // hint
    import'codemirror/addon/hint/show-hint.js'
    import'codemirror/addon/hint/show-hint.css'
    import'codemirror/addon/hint/javascript-hint.js'
    import'codemirror/addon/selection/active-line.js'
    // highlightSelectionMatches
    import'codemirror/addon/scroll/annotatescrollbar.js'
    import'codemirror/addon/search/matchesonscrollbar.js'
    import'codemirror/addon/search/searchcursor.js'
    import'codemirror/addon/search/match-highlighter.js'
    // keyMap
    import'codemirror/mode/clike/clike.js'
    import'codemirror/addon/edit/matchbrackets.js'
    import'codemirror/addon/comment/comment.js'
    import'codemirror/addon/dialog/dialog.js'
    import'codemirror/addon/dialog/dialog.css'
    import'codemirror/addon/search/searchcursor.js'
    import'codemirror/addon/search/search.js'
    import'codemirror/keymap/sublime.js'
    // foldGutter
    import'codemirror/addon/fold/foldgutter.css'
    import'codemirror/addon/fold/brace-fold.js'
    import'codemirror/addon/fold/comment-fold.js'
    import'codemirror/addon/fold/foldcode.js'
    import'codemirror/addon/fold/foldgutter.js'
    import'codemirror/addon/fold/indent-fold.js'
    import'codemirror/addon/fold/markdown-fold.js'
    import'codemirror/addon/fold/xml-fold.js'

    import'codemirror/addon/edit/closetag.js'

    export default {
        components: {
            'app-codemirror': codemirror,
            'app-button': Button,
            'app-modal': Modal,
            'app-field': Field,
            'app-label': Label,
        },

        props: {
            mode: {
                type: String,
                required: false
            },
            label: {
                type: String,
                default: ''
            },
            required: {
                type: Boolean,
                default: false
            },
            description: {
                type: String,
                default: ''
            },
            error: {
                type: String,
                default: ''
            },
            value: {
                default: ''
            },
            name: {
                required: false
            },
            classes: {
                type: Object,
                default: () => ({})
            },
            append: {
                type: String,
                default: ''
            }
        },

        data: () => ({
            content: '',
            draftId: uuidv4(),
            modalOpen: false,
            cmOptions: {
                autoCloseTags: true,
                tabSize: 4,
                styleActiveLine: false,
                lineNumbers: true,
                styleSelectedText: false,
                line: true,
                foldGutter: true,
                gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
                highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
                mode: 'text/javascript',
                hintOptions:{
                    completeSingle: false
                },
                keyMap: "sublime",
                matchBrackets: true,
                showCursorWhenSelecting: true,
                extraKeys: { "Ctrl": "autocomplete" },
            }
        }),

        mounted() {
            if (this.mode) {
                this.cmOptions.mode = this.mode
            }

            if (this.value) {
                this.content = this.value;
            }

            setTimeout(() => {
                this.cmOptions.styleSelectedText =  true,
                this.cmOptions.styleActiveLine = true
            }, 1800)
        },

        computed: {},

        methods: {
            closeModal() {
                this.modalOpen = false;
            },
            handleChange(value) {
                this.content = value;

                this.$emit('input', this.content);
            },
        },

        watch:{
            value(value) {
                this.handleChange(value);
            }
        }
    }

    function uuidv4() {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
        )
    }
</script>

<style>
  .CodeMirror-focused .cm-matchhighlight {
    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAFklEQVQI12NgYGBgkKzc8x9CMDAwAAAmhwSbidEoSQAAAABJRU5ErkJggg==);
    background-position: bottom;
    background-repeat: repeat-x;
  }
  .cm-matchhighlight {background-color: lightgreen}
  .CodeMirror-selection-highlight-scrollbar {background-color: green}
  .CodeMirror {border:1px solid rgb(211,211,211); }

  .CodeMirror {height: 200px !important;}
  .cm-height-200 .CodeMirror {height: 200px !important;}
  .cm-height-500 .CodeMirror {height: 500px !important;}
</style>


// WEBPACK FOOTER //
// src/components/composer/Fields/Code.vue